export const RATE_US_ACTIVE_THRESHOLD_MS = 5 * 60 * 1000;
export const RATE_US_IDLE_TIMEOUT_MS = 30 * 1000;
export const RATE_US_MIN_INTERACTIONS = 2;

const meaningfulRateUsRoutes = new Set([
  "page_facilities_detail",
  "page_thingstodo_detail",
  "page_promotion",
  "page_hotel_info",
  "page_activity",
  "page_assistance",
  "page_dc",
]);

export const isMeaningfulRateUsRoute = (routeName: unknown) =>
  meaningfulRateUsRoutes.has(String(routeName || ""));

interface EngagementState {
  activeMs: number;
  interactions: number;
  lastTickAt: number;
  lastActivityAt: number;
}

const storageKey = (merchantId: string, customerId: string) =>
  `vouch:rate-us:engagement:${merchantId}:${customerId}`;

const defaultState = (now: number): EngagementState => ({
  activeMs: 0,
  interactions: 0,
  lastTickAt: now,
  lastActivityAt: now,
});

const readState = (key: string, now: number): EngagementState => {
  try {
    const stored = JSON.parse(localStorage.getItem(key) || "null");
    if (
      stored &&
      Number.isFinite(stored.activeMs) &&
      Number.isFinite(stored.interactions)
    ) {
      return {
        activeMs: Math.max(0, stored.activeMs),
        interactions: Math.max(0, stored.interactions),
        lastTickAt: now,
        lastActivityAt: now,
      };
    }
  } catch {
    // Corrupt engagement data is safe to discard.
  }
  return defaultState(now);
};

export function isRateUsEngagementEligible(state: {
  activeMs: number;
  interactions: number;
}) {
  return (
    state.activeMs >= RATE_US_ACTIVE_THRESHOLD_MS &&
    state.interactions >= RATE_US_MIN_INTERACTIONS
  );
}

let activeTracker:
  | {
      recordInteraction: () => void;
      stop: () => void;
    }
  | undefined;

export function startRateUsEngagementTracking(params: {
  merchantId: string;
  customerId: string;
  onEligible: () => void;
}) {
  activeTracker?.stop();

  const key = storageKey(params.merchantId, params.customerId);
  const state = readState(key, Date.now());
  let eligibleEmitted = false;

  const persist = () =>
    localStorage.setItem(
      key,
      JSON.stringify({
        activeMs: state.activeMs,
        interactions: state.interactions,
      }),
    );
  const emitIfEligible = () => {
    if (!eligibleEmitted && isRateUsEngagementEligible(state)) {
      eligibleEmitted = true;
      params.onEligible();
    }
  };
  const noteActivity = () => {
    state.lastActivityAt = Date.now();
  };
  const recordInteraction = () => {
    noteActivity();
    state.interactions += 1;
    persist();
    emitIfEligible();
  };
  const tick = () => {
    const now = Date.now();
    const delta = Math.max(0, now - state.lastTickAt);
    state.lastTickAt = now;
    const isForeground = document.visibilityState === "visible";
    const isActive = now - state.lastActivityAt <= RATE_US_IDLE_TIMEOUT_MS;
    if (isForeground && isActive) {
      state.activeMs += delta;
      persist();
      emitIfEligible();
    }
  };
  const resetTick = () => {
    state.lastTickAt = Date.now();
    if (document.visibilityState === "visible") noteActivity();
  };

  ["pointerdown", "keydown", "touchstart", "scroll"].forEach((event) =>
    window.addEventListener(event, noteActivity, { passive: true }),
  );
  document.addEventListener("visibilitychange", resetTick);
  const timer = window.setInterval(tick, 1000);
  emitIfEligible();

  const stop = () => {
    window.clearInterval(timer);
    ["pointerdown", "keydown", "touchstart", "scroll"].forEach((event) =>
      window.removeEventListener(event, noteActivity),
    );
    document.removeEventListener("visibilitychange", resetTick);
    persist();
  };

  activeTracker = { recordInteraction, stop };
  return activeTracker;
}

export function recordRateUsInteraction() {
  activeTracker?.recordInteraction();
}
